import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Lock as LockIcon } from '@mui/icons-material';
import { getNotificationPreferences, updateNotificationPreferences, changePassword } from '../services/api';

function SettingsPage() {
  const [preferences, setPreferences] = useState({
    notification_email: '',
    notification_phone: '',
    email_notifications_enabled: true,
    sms_notifications_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [tempPhone, setTempPhone] = useState('');

  // Password change state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const data = await getNotificationPreferences();
      setPreferences(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load preferences');
      setLoading(false);
    }
  };

  const handleToggleChange = async (field) => {
    const newValue = !preferences[field];
    setPreferences({ ...preferences, [field]: newValue });
    
    try {
      setSaving(true);
      await updateNotificationPreferences({ [field]: newValue });
      setSuccess('Preference updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update preference');
      setPreferences({ ...preferences, [field]: !newValue }); // Revert
    } finally {
      setSaving(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const startEditEmail = () => {
    setTempEmail(preferences.notification_email || '');
    setEditingEmail(true);
  };

  const startEditPhone = () => {
    setTempPhone(preferences.notification_phone || '');
    setEditingPhone(true);
  };

  const saveEmail = async () => {
    try {
      setSaving(true);
      const result = await updateNotificationPreferences({ notification_email: tempEmail });
      setPreferences({ ...preferences, notification_email: result.notification_email });
      setEditingEmail(false);
      setSuccess('Email updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  const savePhone = async () => {
    try {
      setSaving(true);
      const result = await updateNotificationPreferences({ notification_phone: tempPhone });
      setPreferences({ ...preferences, notification_phone: result.notification_phone });
      setEditingPhone(false);
      setSuccess('Phone updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update phone');
    } finally {
      setSaving(false);
    }
  };

  // Password change handlers
  const handleOpenPasswordModal = () => {
    setPasswordModalOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      await changePassword(currentPassword, newPassword);
      handleClosePasswordModal();
      setSuccess('Password changed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.msg || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: { xs: 2, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.email_notifications_enabled}
                  onChange={() => handleToggleChange('email_notifications_enabled')}
                  disabled={!preferences.notification_email || saving}
                />
              }
              label="Email Notifications"
            />
            <Box sx={{ ml: 4, mt: 1 }}>
              {editingEmail ? (
                <TextField
                  size="small"
                  value={tempEmail}
                  onChange={(e) => setTempEmail(e.target.value)}
                  type="email"
                  fullWidth
                  sx={{ maxWidth: 400 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={saveEmail} disabled={saving}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton onClick={() => setEditingEmail(false)}>
                          <CancelIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {preferences.notification_email ? (
                    <>
                      Sending to: {preferences.notification_email}
                      <IconButton size="small" onClick={startEditEmail} sx={{ ml: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      ⚠️ No email on file
                      <Button size="small" onClick={startEditEmail} sx={{ ml: 1 }}>
                        Add Email
                      </Button>
                    </>
                  )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.sms_notifications_enabled}
                  onChange={() => handleToggleChange('sms_notifications_enabled')}
                  disabled={!preferences.notification_phone || saving}
                />
              }
              label="SMS Notifications"
            />
            <Box sx={{ ml: 4, mt: 1 }}>
              {editingPhone ? (
                <TextField
                  size="small"
                  value={tempPhone}
                  onChange={(e) => setTempPhone(e.target.value)}
                  placeholder="6071234567"
                  fullWidth
                  sx={{ maxWidth: 400 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={savePhone} disabled={saving}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton onClick={() => setEditingPhone(false)}>
                          <CancelIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {preferences.notification_phone ? (
                    <>
                      Sending to: {formatPhone(preferences.notification_phone)}
                      <IconButton size="small" onClick={startEditPhone} sx={{ ml: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      ⚠️ No phone on file
                      <Button size="small" onClick={startEditPhone} sx={{ ml: 1 }}>
                        Add Phone
                      </Button>
                    </>
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Security
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={handleOpenPasswordModal}
            >
              Change Password
            </Button>
          </Box>
        </Paper>

        {/* Change Password Modal */}
        <Dialog
          open={passwordModalOpen}
          onClose={handleClosePasswordModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{passwordError}</Alert>
            )}
            <TextField
              margin="dense"
              label="Current Password"
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mt: 1 }}
            />
            <TextField
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Confirm New Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordModal}>Cancel</Button>
            <Button
              onClick={handleChangePassword}
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default SettingsPage;
